import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Menu, MenuItem, Divider, Skeleton } from "@mui/material";
import { ChevronDown, Plus, Check, Settings } from "lucide-react";

const ChannelSwitcher = ({ 
  pages, 
  selectedPage, 
  managedPages,
  onPageSelect, 
  onAddChannel,
  onClearConfiguration
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const currentPage = pages.find(p => p.id === selectedPage);
  const managedPagesList = pages.filter(p => managedPages.includes(p.id));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectPage = async (pageId) => {
    setLoading(true);
    handleClose();
    await onPageSelect(pageId);
    // Small delay to show skeleton
    setTimeout(() => setLoading(false), 800);
  };

  const handleAddChannel = () => {
    handleClose();
    onAddChannel();
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          padding: "6px 12px",
          borderRadius: "8px",
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {loading ? (
          <>
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton variant="text" width={120} height={20} />
            <ChevronDown size={16} />
          </>
        ) : currentPage ? (
          <>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: currentPage.hasInstagram ? "#E1306C" : "#1877F2",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {currentPage.name}
            </Typography>
            <ChevronDown size={16} />
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Select Channel
            </Typography>
            <ChevronDown size={16} />
          </>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400,
            mt: 1,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            MANAGED CHANNELS ({managedPagesList.length})
          </Typography>
          <Box 
            onClick={() => {
              handleClose();
              if (onClearConfiguration) onClearConfiguration();
            }}
            sx={{ 
              cursor: "pointer", 
              color: "text.secondary",
              "&:hover": { color: "primary.main" }
            }}
          >
            <Settings size={14} />
          </Box>
        </Box>

        {managedPagesList.map((page) => (
          <MenuItem
            key={page.id}
            onClick={() => handleSelectPage(page.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1.5,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: page.hasInstagram ? "#E1306C" : "#1877F2",
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: page.id === selectedPage ? 600 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {page.name}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {page.hasInstagram ? "Instagram" : "Facebook"}
              </Typography>
            </Box>
            {page.id === selectedPage && (
              <Check size={16} color="#1976d2" />
            )}
          </MenuItem>
        ))}

        {managedPagesList.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No channels managed yet
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={handleAddChannel}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.5,
            px: 2,
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          <Plus size={18} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Add New Channel
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ChannelSwitcher;
